import React from 'react';

import { Menu, Transition } from '@headlessui/react';

import styles from './Notifications.module.css';
import { useTranslation } from 'react-i18next';
import {
  NotificationDto,
  useNotificationControllerClearNotificationsMutation,
  useNotificationControllerCheckQuery,
} from '@/store/services/MidleNestApi.service';
import { Avatar, DateTime } from '@/components';
import { Link } from 'react-router-dom';
import { useProfile } from '@/hooks';

function NotificationItem({ type, title, message, related, createdAt }: NotificationDto) {
  const { profile } = useProfile();

  let link = '';
  let selectedMessage = '';
  if (type == 10) {
    link = `/clans/${profile?.clan?.id}/join-requests`;
    selectedMessage = related.name || '';
  } else if (type == 11) {
    link = `/clans/${related.id}`;
    selectedMessage = related.name || '';
  } else if (type == 20) {
    link = '/settings/rewards';
    selectedMessage = message;
  }

  if (link)
    return (
      <Menu.Item
        as={Link}
        to={link}
        className='flex relative w-full items-center px-4 py-3 gap-x-3 hover:bg-gray-100'
      >
        <Avatar name='' src={related?.avatar?.src} className='h-10 w-10 sm:h-12 sm:w-12 ' />
        <div className='flex flex-col gap-y-0.5'>
          <h4 className='text-sm leading-4 font-semibold text-blue-600'>{title}</h4>
          <p className='text-xs leading-4 font-normal text-gray-600'>{selectedMessage}</p>
          <DateTime date={createdAt} className='text-xs leading-4 font-medium text-blue-500' />
        </div>
      </Menu.Item>
    );

  return (
    <Menu.Item
      as='div'
      className='flex relative w-full items-center px-4 py-3 gap-x-3 hover:bg-gray-100'
    >
      <Avatar name='' src={related?.avatar?.src} className='h-10 w-10 sm:h-12 sm:w-12 ' />
      <div className='flex flex-col gap-y-0.5'>
        <h4 className='text-sm leading-4 font-semibold text-blue-600'>{title}</h4>
        <p className='text-xs leading-4 font-normal text-gray-600'>{selectedMessage}</p>
        <DateTime date={createdAt} className='text-xs leading-4 font-medium text-blue-500' />
      </div>
    </Menu.Item>
  );
}

export default function Notifications() {
  const { t } = useTranslation();
  const { data, isLoading } = useNotificationControllerCheckQuery(
    { limit: 25 },
    { pollingInterval: 30 * 1000 }, // 30 seconds
  );

  const [clearNotifications, { isLoading: clearing }] =
    useNotificationControllerClearNotificationsMutation();

  const notifications = React.useMemo(() => data?.list, [data]);

  const buttonDisabled = React.useMemo(
    () => isLoading || clearing || !notifications || notifications.length < 1,
    [isLoading, clearing, notifications],
  );

  const clearNotificationsHandler = React.useCallback(async () => {
    if (!notifications || notifications.length < 1) return;
    const notificationIdList = notifications.map((x) => x.id.toString());
    await clearNotifications({ list: notificationIdList });
  }, [notifications]);

  return (
    <Menu as='div' className='relative inline-block text-left z-10'>
      <div className='relative flex justify-center'>
        <Menu.Button className='w-12 h-12 flex items-center justify-center rounded-full text-md text-gray-800 dark:text-white transition-all'>
          <span className='sr-only'>{t('Notifications')}</span>
          <i className='icon-bell' />
        </Menu.Button>
        {data?.list && data?.list.length > 0 && (
          <div className='badge badge-xs badge-red border border-white dark:border-none dark:!bg-[#FFDA68] w-2.5 h-2.5 dark:w-2 dark:h-2 !p-0 dark:text-black absolute flex justify-center top-3.5 dark:top-4 left-6 pointer-events-none' />
        )}
      </div>

      <Transition
        as={React.Fragment}
        enter='transition ease-out duration-100'
        enterFrom='transform opacity-0 scale-95'
        enterTo='transform opacity-100 scale-100'
        leave='transition ease-in duration-75'
        leaveFrom='transform opacity-100 scale-100'
        leaveTo='transform opacity-0 scale-95'
      >
        <Menu.Items
          className={
            'absolute right-0 mt-4 w-80 origin-top-right rounded-md bg-white dark:bg-[#202020] max-h-96 shadow-lg ring-1 ring-white/50 dark:ring-[#5E5331] focus:outline-none overflow-x-hidden overflow-y-auto ' +
            styles['menu-caret']
          }
        >
          <header className='flex justify-between items-center p-4'>
            <h3 className='text-sm font-semibold dark:text-white'>{t('Notifications')}</h3>
            <button
              className='text-xs leading-5 font-regular text-red-500 disabled:grayscale dark:disabled:opacity-50 dark:disabled:grayscale-0'
              disabled={buttonDisabled}
              onClick={clearNotificationsHandler}
            >
              {t('Clear All')}
            </button>
          </header>
          {notifications && notifications.map((x, idx) => <NotificationItem key={idx} {...x} />)}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
